<template>
  <AppModal :mode="mode" title="Add Contact" @close="close" width="max-w-6xl">
    <template #body>
      <div class="grid gap-6 sm:gap-4">
        <AppCard
          :mode="mode"
          title="Name Settings"
          :isCenter="false"
          :isSmallTitle="true"
        >
          <template #body>
            <div
              class="grid grid-cols-4 gap-x-3 gap-y-4 md:grid-cols-2 sm:grid-cols-1 sm:gap-4"
            >
              <AppInput
                :mode="mode"
                id="first-name"
                title="First name"
                placeholder="Not specified"
                v-model="contactData.first_name"
              />
              <AppInput
                :mode="mode"
                id="last-name"
                title="Last name"
                placeholder="Not specified"
                v-model="contactData.last_name"
              />
              <AppInput
                :mode="mode"
                id="title"
                title="Title"
                placeholder="Not necessarily"
                v-model="contactData.title"
              />
              <AppInput
                :mode="mode"
                id="username"
                title="Username"
                placeholder="Not specified"
                v-model="contactData.username"
              />
            </div>
          </template>
        </AppCard>
        <AppCard
          :mode="mode"
          title="Phone Numbers"
          :isCenter="false"
          :isSmallTitle="true"
        >
          <template #body>
            <div
              class="grid grid-cols-3 gap-x-3 gap-y-4 md:grid-cols-2 sm:grid-cols-1 sm:gap-4"
            >
              <AppInput
                :mode="mode"
                type="tel"
                id="work-phone"
                title="Work phone"
                placeholder="Not specified"
                v-model="contactData.work_phone"
              />
              <AppInput
                :mode="mode"
                type="tel"
                id="home-phone"
                title="Home phone"
                placeholder="Not specified"
                v-model="contactData.home_phone"
              />
              <AppInput
                :mode="mode"
                type="tel"
                id="mobile-phone"
                title="Mobile phone"
                placeholder="Not specified"
                v-model="contactData.mobile_phone"
              />
              <AppInput
                :mode="mode"
                type="tel"
                id="fax"
                title="Fax"
                placeholder="Not specified"
                v-model="contactData.fax"
              />
              <AppInput
                :mode="mode"
                type="tel"
                id="other-phone"
                title="Other phone"
                placeholder="Not specified"
                v-model="contactData.other_phone"
              />
              <AppInput
                :mode="mode"
                id="toll"
                title="Toll free"
                placeholder="Not specified"
                v-model="memberData.toll_free_phone"
                :isDisabled="true"
              />
            </div>
          </template>
        </AppCard>
        <AppCard
          :mode="mode"
          title="Address Information"
          :isCenter="false"
          :isSmallTitle="true"
        >
          <template #body>
            <div
              class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4"
            >
              <AppInput
                :mode="mode"
                id="address-1"
                title="Address 1"
                placeholder="Not specified"
                v-model="contactData.address1"
              />
              <AppInput
                :mode="mode"
                id="address-2"
                title="Address 2"
                placeholder="Not specified"
                v-model="contactData.address2"
              />
              <div class="col-span-2 sm:col-span-1">
                <div
                  class="grid grid-cols-4 gap-x-3 gap-y-4 md:grid-cols-2 sm:grid-cols-1 sm:gap-4"
                >
                  <AppSelect
                    :mode="mode"
                    title="Country"
                    v-model="contactData.country"
                  >
                    <template #options>
                      <option value="">Country</option>
                      <option
                        v-for="(country, key) in countries"
                        :value="country.code"
                        :key="key"
                        :selected="country.code === contactData.country"
                      >
                        {{ country.country }}
                      </option>
                    </template>
                  </AppSelect>
                  <AppInput
                    :mode="mode"
                    id="zip"
                    title="Zip"
                    placeholder="Not specified"
                    v-model="contactData.zip"
                  />
                  <AppSelect
                    :mode="mode"
                    title="City"
                    v-model="contactData.city"
                    :isDisabled="cities.length === 0"
                  >
                    <template #options>
                      <option value="">City</option>
                      <option
                        v-for="(city, key) in cities"
                        :value="city.name"
                        :key="key"
                        :selected="city.name === contactData.city"
                      >
                        {{ city.name }}
                      </option>
                    </template>
                  </AppSelect>
                  <AppSelect
                    :mode="mode"
                    title="State"
                    v-model="contactData.state"
                    :isDisabled="states.length === 0"
                  >
                    <template #options>
                      <option value="">State</option>
                      <option
                        v-for="(state, key) in states"
                        :value="state.code"
                        :key="key"
                        :selected="state.code === contactData.state"
                      >
                        {{ state.name }}
                      </option>
                    </template>
                  </AppSelect>
                </div>
              </div>
            </div>
          </template>
        </AppCard>
        <AppCard
          :mode="mode"
          title="Other Communication Channels"
          :isCenter="false"
          :isSmallTitle="true"
        >
          <template #body>
            <div
              class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4"
            >
              <AppInput
                :mode="mode"
                type="email"
                id="email"
                title="Email"
                placeholder="Not specified"
                v-model="contactData.email"
              />
              <AppInput
                :mode="mode"
                id="linkedin"
                title="Linkedin"
                placeholder="Not specified"
                v-model="contactData.linkedin_url"
              />
              <div class="col-span-2 sm:col-span-1">
                <AppCheckbox
                  :mode="mode"
                  id="out-office"
                  v-model="contactData.on_vacation"
                  title="I am out of the office, do not email"
                />
              </div>
            </div>
          </template>
        </AppCard>
        <AppButton title="Add my information" @action="addContact" />
      </div>
    </template>
  </AppModal>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import AppModal from "@C/Common/AppModal.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "AddContactSettings",
  props: {
    member: {
      type: Object,
      default: null,
    },
    countries: {
      type: Array,
      default: [],
    },
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  components: {
    AppModal,
    AppInput,
    AppCard,
    AppCheckbox,
    AppSelect,
    AppButton,
  },
  data() {
    return {
      contactData: {
        address1: this.member.address1 || "",
        address2: this.member.address2 || "",
        administrator: false,
        billing_contact: false,
        board_end_date: "",
        board_start_date: "",
        board_type: null,
        chair: null,
        city: this.member.city || "",
        contact_id: "",
        country: this.member.country || "",
        default_email_is_buy: true,
        email: this.member.company_email || "",
        email_is_valid: true,
        facebook_url: "",
        fax: this.member.fax || "",
        first_name: "",
        home_phone: this.member.company_phone1 || "",
        im_name: "",
        last_login_date: "",
        last_mfr_id: null,
        last_name: "",
        linkedin_url: "",
        mobile_phone: this.member.company_phone2 || "",
        notes: null,
        on_vacation: false,
        other_phone: "",
        press: false,
        primary_contact: true,
        rec_leads: true,
        rec_news_letter: true,
        state: this.member.state_region || "",
        title: "",
        username: "",
        work_phone: "",
        zip: this.member.zip || "",
      },
      memberData: {},
      states: [],
      cities: [],
      debounceTimer: null,
    };
  },
  mounted() {
    this.memberData = {
      member_id: this.member.member_id,
      toll_free_phone: this.member.toll_free_phone,
    };
    if (this.contactData.country && this.contactData.zip) {
      this.debouncedGetLocations();
    }
  },
  methods: {
    async addContact() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact: this.contactData,
        member: this.memberData,
      };
      try {
        const { data } = await ApiRequest().post(`/contact`, payload);
        if (data) {
          this.toastStore.show("Contact addded successfully!", "success");
          this.contactAdded();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getLocations() {
      if (!this.contactData.country || !this.contactData.zip) {
        this.states = [];
        this.cities = [];
        return;
      }
      try {
        const payload = {
          country_code: this.$getCountryCode(
            this.countries,
            this.contactData.country,
          ),
          postal_code: this.contactData.zip,
        };
        const response = await fetch("/public_api/postal_codes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          this.toastStore.show("Failed to fetch location data", "error");
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show("No location data found", "error");
          this.states = [];
          this.cities = [];
          return;
        }
        const statesMap = new Map();
        const citiesMap = new Map();
        data.forEach((item) => {
          const source = item._source;
          if (source.admin_code1) {
            statesMap.set(source.admin_code1, {
              code: source.admin_code1,
              name: source.admin_name1 || source.admin_code1,
            });
          }
          if (source.place_name) {
            citiesMap.set(source.place_name, {
              name: source.place_name,
            });
          }
        });
        this.states = Array.from(statesMap.values());
        this.cities = Array.from(citiesMap.values());
      } catch (e) {
        this.handleError(e);
        this.states = [];
        this.cities = [];
      }
    },
    debouncedGetLocations() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.getLocations();
      }, 500);
    },
    close() {
      this.contactData = {
        address1: this.member.address1 || "",
        address2: this.member.address2 || "",
        administrator: false,
        billing_contact: false,
        board_end_date: "",
        board_start_date: "",
        board_type: null,
        chair: null,
        city: this.member.city || "",
        contact_id: "",
        country: this.member.country || "",
        default_email_is_buy: true,
        email: this.member.company_email || "",
        email_is_valid: true,
        facebook_url: "",
        fax: this.member.fax || "",
        first_name: "",
        home_phone: this.member.company_phone1 || "",
        im_name: "",
        last_login_date: "",
        last_mfr_id: null,
        last_name: "",
        linkedin_url: "",
        mobile_phone: this.member.company_phone2 || "",
        notes: null,
        on_vacation: false,
        other_phone: "",
        press: false,
        primary_contact: true,
        rec_leads: true,
        rec_news_letter: true,
        state: this.member.state_region || "",
        title: "",
        username: "",
        work_phone: "",
        zip: this.member.zip || "",
      };
      this.states = [];
      this.cities = [];
      this.debounceTimer = null;
      this.$emit("close");
    },
    contactAdded() {
      this.close();
      this.$emit("contactAdded");
    },
  },
  watch: {
    "contactData.country": function (newVal) {
      if (newVal && this.contactData.zip) {
        this.debouncedGetLocations();
      }
    },
    "contactData.zip": function (newVal) {
      if (newVal && this.contactData.country) {
        this.debouncedGetLocations();
      }
    },
  },
};
</script>
